import { action, autorun, computed, observable } from 'mobx';
import { AlertHandle, autostore, bind, Clock, IError, NotificationHandle } from 'Mri.Web.Common';
import {LiveChartGridOptions} from "../Displays/LiveCharts/LiveChartsGridSelector";

export class AppStore {
    public constructor() {
        if (typeof window != 'undefined') {
            this.innerWidth = window.innerWidth;
            window.addEventListener(
                'resize',
                action(() => (this.innerWidth = window.innerWidth))
            );

            window.addEventListener('storage', this.localStorageUpdated);
        }
        if(!this.storage.liveChartSettings) {
            this.storage.liveChartSettings = new LiveChartSettings();
        }
        autostore(this.storage, 'AppStore.AppStorage');
        autorun(this.setTheme);
    }

    @bind
    @action
    public setError(error: IError | Error) {
        if (error instanceof Error) {
            this.error = {
                message: error.message,
                file: error.name,
                stack: error.stack,
            };
        } else {
            this.error = error;
        }
    }

    @bind
    @action
    public hideHeader() {
        this.hiddenHeader = true;
    }

    @bind
    @action
    public showHeader() {
        this.hiddenHeader = false;
    }

    @bind
    private setTheme() {
        if (typeof document == 'undefined') return;

        const el = document.getElementById('MainBody');

        if (el && this.storage.theme) {
            el.classList.remove('theme-1');
            el.classList.remove('theme-2');
            el.classList.remove('dark-theme');

            el.classList.add(this.storage.theme);
        }
    }

    @bind
    private localStorageUpdated() {
        const json = localStorage.getItem('AppStore.AppStorage');
        if (!json) return;

        const obj = JSON.parse(json);

        if (obj.version != this.storage.version) {
            window.location.reload();
        }
    }

    @computed
    public get isMobile() {
        return this.innerWidth < 767;
    }

    @computed
    public get isMedium() {
        return this.innerWidth < 992;
    }

    @computed
    public get isLarge() {
        return this.innerWidth < 1200;
    }

    @observable
    public storage = new AppStorage();
    @observable
    public alertHandle = new AlertHandle();
    @observable
    public notificationHandle = new NotificationHandle();
    @observable
    public mainContainer: HTMLDivElement | null = null;
    @observable
    public innerWidth = 0;
    @observable
    public hiddenHeader = false;
    @observable
    public clock = new Clock();
    @observable
    public error?: IError;
}

class AppStorage {
    public static version = '06-27-25.1';

    public isValid() {
        return this.version == AppStorage.version;
    }

    @observable
    public version = AppStorage.version;
    @observable
    public theme: 'theme-1' | 'theme-2' | 'dark-theme' = 'theme-1';
    @observable
    public sidebarCollapsed = false;
    @observable
    public tableView = true;
    @observable
    public displayLogTimes: { start: Date | undefined; end: Date | undefined } | undefined = {
        start: undefined,
        end: undefined,
    };
    @observable
    public liveChartSettings = new LiveChartSettings();
    @observable
    public lastMainContainerScrollPositionX: number = 0;
    @observable
    public lastMainContainerScrollPositionY: number = 0;
    @observable
    public sortedColumn: string = '';
    @observable
    public documentsSearch = '';
}

class LiveChartSettings {
    @observable
    public liveChartTimeWindow: 1 | 2 | 4 | 8 | 12 | 24 = 2;
    @observable
    public liveChartSelectedChartName: string = '';
    @observable
    public liveChartSelectedGrid: LiveChartGridOptions = LiveChartGridOptions.FourByFour;
    @observable
    public liveChartHideLegend: boolean = true;
    @observable
    public liveChartHide: boolean = false;
    @observable
    public secsPerSample: number = 10;
    @observable
    public triggerSecsPerSampleUpdate: boolean = true;
}
